import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getLocalAuth, destroyLocalAuth } from 'utils/auth';

/**
 * Contexts & Context Providers
 */
import { AppContext } from 'contexts/AppContext';
import { UserContextProvider } from 'contexts/UserContext';
import { AvailableUnitContextProvider } from 'contexts/AvailableUnitContext';
import { PaymentContextProvider } from 'contexts/PaymentContext';
import { useModal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Portal';
import HavingTroubleLoggingInModal from 'components/Molecules/HavingTroubleLoggingInModal';
import havingTroubleLoggingInModalMessages from 'components/Molecules/HavingTroubleLoggingInModal/messages';

/**
 * Pages

 */
import Login from 'pages/Login';
import Register from 'pages/Register';
import ForgotPassword from 'pages/ForgotPassword';
import ResetPassword from 'pages/ResetPassword';
import VerifyEmailAddress from 'pages/VerifyEmailAddress';
import ClaimAccount from 'pages/ClaimAccount';
import Portal from 'pages/Portal';
import FusionAuthLogin from 'pages/Login/FusionAuthLogin';
import Maintenance from '../Maintenance';
import { ProspectPreferenceContextProvider } from 'contexts/ProspectPreferenceContext';
import { LeaseContextProvider } from 'contexts/LeaseContext';
import { IntentContextProvider } from 'contexts/IntentContext';
import { deleteLocalTellMeIntentData, deleteLocalUnitIntentData } from 'utils';
import ContactUs from 'pages/ContactUs';

const Gateway = ({ match }) => {
  const { organizationId, propertyId, authenticationPortal } =
    useContext(AppContext);
  const { authToken, authUser } = getLocalAuth();
  const modalProps = useModal(false);
  const { maintenanceMode, contactUsWidget } = useFlags();
  const history = useHistory();
  const isLogin = !history.location.pathname.endsWith('register');

  const isAuthenticated =
    authToken &&
    authUser?.id &&
    propertyId?.localeCompare(authUser.propertyId, 'en', {
      sensitivity: 'base',
    }) === 0 &&
    organizationId?.localeCompare(authUser.organizationId, 'en', {
      sensitivity: 'base',
    }) === 0;

  // Delete intent data when navigation away from page
  // May be unreliable on mobile [https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#usage_notes]
  useEffect(() => {
    if (!isAuthenticated) {
      window.addEventListener(
        'beforeunload',
        () => {
          deleteLocalUnitIntentData();
          deleteLocalTellMeIntentData();
        },
        { once: true },
      );
    }
  }, [history, isAuthenticated]);

  if (isAuthenticated) {
    return (
      <UserContextProvider>
        <ProspectPreferenceContextProvider>
          <AvailableUnitContextProvider>
            <LeaseContextProvider>
              <IntentContextProvider>
                <PaymentContextProvider>
                  <Switch>
                    <Route
                      exact
                      path={`${match.url}/verify/:verifyToken*`}
                      component={
                        authenticationPortal
                          ? FusionAuthLogin
                          : VerifyEmailAddress
                      }
                    />
                    <Route path={`${match.url}`} component={Portal} />
                  </Switch>
                </PaymentContextProvider>
              </IntentContextProvider>
            </LeaseContextProvider>
          </AvailableUnitContextProvider>
        </ProspectPreferenceContextProvider>
      </UserContextProvider>
    );
  }

  /**
   * Destroys any lingering artifacts of a previous Authenticated Session.
   * This typically happens:
   *  - on a fresh visit
   *  - when the authenticated token has expired
   *  - when visiting a Resident Portal for a different property
   */
  destroyLocalAuth();
  return maintenanceMode ? (
    <Maintenance />
  ) : (
    <>
      <Switch>
        <Route
          exact
          path={`${match.url}/forgot-password`}
          component={authenticationPortal ? FusionAuthLogin : ForgotPassword}
        />
        <Route
          exact
          path={`${match.url}/forgot-password/:resetToken*`}
          component={authenticationPortal ? FusionAuthLogin : ResetPassword}
        />
        <Route
          exact
          path={`${match.url}/verify/:verifyToken*`}
          component={
            authenticationPortal ? FusionAuthLogin : VerifyEmailAddress
          }
        />
        <Route
          exact
          path={`${match.url}/claim/:claimToken*`}
          component={authenticationPortal ? FusionAuthLogin : ClaimAccount}
        />
        <Route
          exact
          path={`${match.url}/register`}
          render={(props) =>
            authenticationPortal ? (
              <FusionAuthLogin {...props} />
            ) : (
              <Register
                {...props}
                handleOpenTroubleLoggingInModal={modalProps.openModal}
              />
            )
          }
        />
        <Route
          exact
          path={`${match.url}`}
          render={(props) =>
            authenticationPortal ? (
              <FusionAuthLogin {...props} />
            ) : (
              <Login
                {...props}
                handleOpenTroubleLoggingInModal={modalProps.openModal}
              />
            )
          }
        />
        {contactUsWidget && (
          <Route exact path={`${match.url}/contact-us`} component={ContactUs} />
        )}
        <Route render={() => <Redirect to={`${match.url}`} />} />
      </Switch>

      <HavingTroubleLoggingInModal
        title={
          isLogin
            ? havingTroubleLoggingInModalMessages.loginTitle
            : havingTroubleLoggingInModalMessages.registerTitle
        }
        {...modalProps}
      />
    </>
  );
};

Gateway.propTypes = {
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default Gateway;
